import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { PostTableRow } from '@app/models/post/PostResponse';
import { getPostTableData, deletePost } from '@app/api/post.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { Pagination } from '@app/models/pagination.model';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export const PostTable: React.FC = () => {
  const [tableData, setTableData] = useState<{
    data: PostTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const { t } = useTranslation();

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((prev) => ({ ...prev, loading: true }));
      getPostTableData({ ...pagination, ...filters })
        .then((res) => {
          if (isMounted.current) {
            setCategoryOptions(res.category_options);
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
          }
        })
        .catch((error) => {
          notificationController.error({ message: error.message });
          setTableData({ data: [], pagination: initialPagination, loading: false });
        });
    },
    [filters, isMounted],
  );

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      ...filters,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => navigate('/view-post', { state: { id } });
  const handleEditRow = (id: number) => navigate('/edit-post', { state: { id } });
  const [categoryOptions, setCategoryOptions] = useState<{ value: number; label: string }[]>([]);

  const handleDeleteRow = async (id: number) => {
    try {
      await deletePost(id); // Call the deletePost function here
      notificationController.success({ message: 'Post deleted successfully!' });
      fetch(tableData.pagination); // Re-fetch the data after deletion
    } catch (error) {
      notificationController.error({ message: 'Failed to delete the post' });
    }
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => (
    <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
      {`Showing ${range[0]} - ${range[1]} of ${total} Posts`}
    </span>
  );

  const searchData = [
    { name: 'search', placeholder: 'Search Title or Slug', type: 'text' as const },
    { name: 'status', placeholder: 'Status', type: 'dropdown' as const },
  ];

  const columns: ColumnsType<PostTableRow> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Category',
      dataIndex: 'post_category_id', // Use post_category_id
      key: 'category_name',
      render: (postCategoryId: number) => {
        const category = categoryOptions.find((option) => option.value === postCategoryId);
        return category ? category.label : 'N/A'; // If category is not found, show 'N/A'
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              type="text"
              onClick={() => handleViewRow(record.id)}
              icon={<EyeOutlined />}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              onClick={() => handleEditRow(record.id)}
              icon={<EditOutlined />}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure to delete this post?"
              onConfirm={() => handleDeleteRow(record.id)} // Call the delete function on confirmation
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleAddPost = () => navigate('/add-post');

  return (
    <BaseCard id="post-table" title="Posts" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true }}
      />
    </BaseCard>
  );
};
